import { AuthLayout } from '../../ui/AuthLayout/AuthLayout';
import { AuthStep } from '../../ui/AuthStep/AuthStep';
import { FormikForm } from '@/components/form/FormikForm/FormikForm';
import { useFormik } from 'formik';
import { Button } from '@/components/Button/Button';
import { Box } from '@/components/Box/Box';
import { AuthTitle } from '../../ui/AuthTitle/AuthTitle';
import { AuthMessage } from '../../ui/styles';
import { useMutation } from '@tanstack/react-query';
import { personalDetailsVerification } from '@/bundle/Auth/LoginFlow/PersonalDetailsVerificationPage/api';
import { useAuthNavigateToStep } from '@/bundle/Auth/hooks/useAuthNavigateToStep';
import { PersonalDetailsVerificationPayload } from '@/api/v1/users/sendPersonalDetailsVerificationApi';
import { authTokenService } from '@/bundle/shared/services/authTokenService';
import { AuthTokenType } from '@/types/sharedTypes';
import { decodeToken } from '@/helpers/tokenHelpers';
import { Notification } from '@/components/Notification/Notification';
import { getResponseError } from '@/helpers/apiHelpers/responseHelpers';
import { PERSONAL_DETAILS_VALIDATION_SCHEMA } from '@/const/formConfigs';
import { FormikInput } from '@/components/form/fields/FormikInput/FormikInput';
import { useTranslation } from 'react-i18next';
import { useAuthNavigateToLogin } from '@/bundle/Auth/hooks/useAuthNavigateToLogin';

export const PersonalDetailsVerificationPage = () => {
  const navigateToStep = useAuthNavigateToStep();
  const { t } = useTranslation(['login', 'form']);
  const { authNavigateToLogin } = useAuthNavigateToLogin();

  const token = authTokenService?.getTokens();

  const { first_name, middle_name, last_name } = decodeToken<AuthTokenType>(token?.access);

  const { data, mutate, isPending } = useMutation({
    mutationKey: ['personal_data_verification'],
    mutationFn: (payload: PersonalDetailsVerificationPayload) => {
      return personalDetailsVerification(payload as PersonalDetailsVerificationPayload);
    },
    onSuccess(response) {
      if (response?.error) return;

      navigateToStep(response?.body);
    },
  });

  const authMessage = t('login:personalDetailsAuthMessage');

  const formik = useFormik({
    initialValues: {
      first_name,
      middle_name,
      last_name,
    },
    validationSchema: PERSONAL_DETAILS_VALIDATION_SCHEMA,
    onSubmit: (values: PersonalDetailsVerificationPayload) => {
      mutate(values);
    },
  });

  const apiError = data?.error;
  const formError = getResponseError(apiError);

  return (
    <AuthLayout>
      <AuthStep>
        <AuthTitle>{t('login:personalDetailsHeader')}</AuthTitle>
        <Box marginBottom='36px'>
          <AuthMessage>{authMessage}</AuthMessage>
        </Box>
        {formError && (
          <Notification variant='error' marginBottom='24px'>
            {formError}
          </Notification>
        )}

        <FormikForm value={formik}>
          <FormikInput
            name='first_name'
            label={t('form:label.firstName')}
            placeholder={t('form:placeholder.firstName')}
            apiError={apiError}
          />
          <FormikInput
            name='middle_name'
            label={t('form:label.middleName')}
            placeholder={t('form:placeholder.middleName')}
            apiError={apiError}
          />
          <FormikInput
            name='last_name'
            label={t('form:label.lastName')}
            placeholder={t('form:placeholder.lastName')}
            apiError={apiError}
          />

          <Box display='flex' flexDirection='column' rowGap='12px' marginTop='14px'>
            <Button type='submit' isLoading={isPending}>
              {t('login:submitButtonLabel')}
            </Button>
            <Button variant='secondary' onClick={authNavigateToLogin}>
              {t('login:returnToLoginButtonLabel')}
            </Button>
          </Box>
        </FormikForm>
      </AuthStep>
    </AuthLayout>
  );
};
